import { useCallback, useRef, useState } from 'react';
import {
  V141_NOTE_MODE,
  type NotePhrasesView,
  type NoteSettings,
  type PracticeNoteSettings,
  type QuickItem,
} from '@ehr/shared';
import type { InterventionHide } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import {
  EMPTY_INTERVENTION_HIDE,
  fixHidden,
  hiddenFor,
  sameHidden,
  saveHidden,
} from '../lib/interventionHide.js';
import type { SideCardsTab } from '../lib/quickSettings.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Modal } from './Modal.js';
import { QuickSettingsWindow } from './QuickAddCard.js';
import { HelpersWindow } from './HelpersWindow.js';
import { PracticeNoteSettingsSection, practiceNotesPatch } from './PracticeNoteSettings.js';
import { DischargePracticeSettings } from './DischargePracticeSettings.js';
import { DefaultCodeRow as KindCodeRow } from './DefaultCodeRow.js';
import type { KindSettings } from '../lib/skillsMedNote.js';
import { DefaultCodeRow } from './CaseNoteSettings.js';
import { defaultCodeOf, type RailSwitch } from '../lib/caseManagementNote.js';

/**
 * ehr#437 reopen (PROGRESS_NOTE_10-14-V81 Note settings, the cog by Clear note), reworked to
 * PROGRESS-NOTE-10-20-V141 (ehr#682): the Note tab has "Generate notes with AI" On / Off and the
 * keyboard shortcuts. V141 removed Writing style, Note mode (Guided, Standard, Expert), Box
 * prompts, the six switches and the "How do you want to write notes?" picker (V120, V125, V126);
 * every note runs V141_NOTE_MODE. Saved for the signed-in person (users."notePrefs".noteSettings).
 */
/** V81 BOX_EXAMPLES: Box prompts "Examples" (Guided); other boxes keep their hint. */
export const BOX_EXAMPLES: Record<string, string> = {
  subjective:
    'For example: Client reported sleeping about 5 hours and two panic episodes since last session.',
  objective:
    'For example: Alert and oriented, affect anxious but congruent, practiced breathing with coaching.',
  assessment:
    'For example: Some progress on coping skills; panic frequency unchanged. Weekly therapy is still needed to bring panic episodes down.',
  plan: 'For example: Continue weekly CBT, practice breathing twice a day, review warning signs next session.',
  data: 'For example: Client reported two panic episodes; alert and oriented, affect anxious.',
  behavior: 'For example: Client presented as anxious and described two panic episodes at work.',
  intervention:
    'For example: Clinician coached diaphragmatic breathing and reviewed the panic cycle.',
  response: 'For example: Client practiced the skill in session and agreed to daily practice.',
};

export function NoteSettingsWindow({
  view,
  current,
  onClose,
  onSaved,
  onShortcuts,
  interventions,
  onInterventions,
  quick,
  startTab,
  aiBySupervisor = false,
  practice,
  onReport,
  hidden,
  onHidden,
  contactId,
  contactName = '',
  initialTab,
  review,
  kindSettings,
  goalRules = true,
  caseGoals = false,
  defaultCode,
}: {
  /** The tab it opens on (Interventions only for admins and managers). */
  initialTab?: 'note' | 'inter';
  view: NotePhrasesView;
  /** What the note uses now (v141NoteSettings). */
  current: NoteSettings;
  onClose: () => void;
  onSaved: (v: NotePhrasesView) => void;
  onShortcuts: () => void;
  /** ehr#437 (V81 Interventions tab): the practice's list; given to admins and managers only. */
  interventions?: string[];
  onInterventions?: (list: string[]) => void;
  /** ehr#682 (V141 V104): the Quick add tab, for this note's client. */
  quick: QuickTabProps;
  /** ehr#682 (V141 V93): the Quick add card's cog opens straight to its tab. */
  startTab?: NoteSettingsTab;
  /** ehr#682 (V141 V126): an associate's AI is set by their supervisor (shown, not changed). */
  aiBySupervisor?: boolean;
  /** ehr#682 (V141 V135): Practice note settings, for admins, managers and supervisors. */
  practice?: {
    value: PracticeNoteSettings;
    onSaved: (v: PracticeNoteSettings) => void;
    /** ehr#708: on a discharge summary, its goal rule and No treatment plan on file reasons too. */
    discharge?: boolean;
  };
  /** ehr#682: open Sign timeliness or Supervision (this window closes first). */
  onReport?: (kind: 'timeliness' | 'supervision') => void;
  /**
   * ehr#682 (V141 V127): Show / hide per intervention, saved for this client or for all clients.
   * Given with the list (admins and managers) on a client's note.
   */
  hidden?: InterventionHide;
  onHidden?: (h: InterventionHide) => void;
  contactId?: string;
  contactName?: string;
  /**
   * ehr#682 (PROGRESS-NOTE V141 V92): on a note in review, signed or co-signed after review, the
   * Supervisor review section: the Show changes / Final text switch (applies at once) and
   * Comment history (this window closes, asking first about unsaved settings).
   */
  review?: {
    view: 'changes' | 'final';
    onView: (v: 'changes' | 'final') => void;
    onHistory: () => void;
  };
  /**
   * ehr#729: a kind's own tabs (skillsMedNote kindSettingsOf). A Skills / rehab note has the Note
   * tab only, so no tab strip.
   */
  kindSettings?: KindSettings;
  /**
   * ehr#724 (ADMIN_PHONE_NOTE_10-30-V7 V4): false on a note with nothing to address (Admin /
   * phone): Practice note settings leaves out the problems, goals and objectives rule.
   */
  goalRules?: boolean;
  /**
   * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9 20 A): a case management note's settings: Practice note
   * settings has Show treatment plan goals.
   */
  caseGoals?: boolean;
  /**
   * ehr#725 (V9 14 A): the note kind's Default billing code row in Practice note settings, for
   * admins and managers (PUT /practice/clinical serviceCodes). `codes`: the practice's now.
   * ehr#729 (SKILLS_REHAB_NOTE_10-30-V7 V3): a Skills / rehab note's default code too.
   */
  defaultCode?: {
    kind: string;
    codes: Partial<Record<string, string>>;
    onSaved: (codes: Partial<Record<string, string>>) => void;
  };
}) {
  const admin = Boolean(interventions && onInterventions);
  // ehr#725: the Default billing code row (No default is '')
  const codeWas = defaultCode ? defaultCodeOf(defaultCode.kind, defaultCode.codes) : '';
  const [code, setCode] = useState(codeWas);
  const codeChanged = !!defaultCode && code !== codeWas;
  const hideOn = admin && Boolean(hidden && onHidden && contactId);
  // ehr#682: the Quick add card opens on Quick add; Interventions only for admins and managers
  const tabs = noteSettingsTabs(admin, Boolean(quick.sideCards), kindSettings);
  const first: NoteSettingsTab = startTab ?? initialTab ?? 'note';
  const [tab, setTab] = useState<NoteSettingsTab>(tabs.some(([k]) => k === first) ? first : 'note');
  // ehr#682: leaving the Quick add tab with changes not saved asks first, as closing it does
  const qaLeave = useRef<((go: () => void) => void) | null>(null);
  const pickTab = (k: NoteSettingsTab) => {
    if (k === tab) return;
    if (tab === 'qa' && qaLeave.current) qaLeave.current(() => setTab(k));
    else setTab(k);
  };
  const [inter, setInter] = useState<string[]>(() => interventions ?? []);
  // ehr#682: what each row was called when the window opened (null: added here), so a rename keeps
  // its hidden state for every client and a delete takes it off every client's hidden list
  const [origin, setOrigin] = useState<(string | null)[]>(() => interventions ?? []);
  const [hideD, setHideD] = useState<string[]>(() => (hideOn ? hiddenFor(hidden, contactId!) : []));
  const renames = Object.fromEntries(
    origin.flatMap((o, i) => (o && o !== inter[i] ? [[o, inter[i]!]] : [])),
  );
  const deleted = new Set((interventions ?? []).filter((o) => !origin.includes(o)));
  const hideBase = hideOn ? fixHidden(hidden!, renames, deleted) : EMPTY_INTERVENTION_HIDE;
  const hideChanged = hideOn && !sameHidden(hideD, hiddenFor(hideBase, contactId!));
  const clientFirst = contactName.split(' ')[0] || 'this client';
  const [iEdit, setIEdit] = useState<{ at: number; text: string } | null>(null);
  const [iDel, setIDel] = useState<number | null>(null);
  const [iNew, setINew] = useState('');
  const [iErr, setIErr] = useState('');
  const clean = (t: string) => t.replace(/\s+/g, ' ').trim().slice(0, 60);
  const taken = (t: string, except = -1) =>
    inter.some((x, i) => i !== except && x.toLowerCase() === t.toLowerCase());
  const addInter = () => {
    const t = clean(iNew);
    if (!t) return;
    if (taken(t)) return setIErr(`${t} is already on the list.`);
    if (inter.length >= 60) return setIErr('The list holds up to 60.');
    setInter((l) => [...l, t]);
    setOrigin((l) => [...l, null]);
    setINew('');
    setIErr('');
  };
  const saveEdit = () => {
    if (!iEdit) return;
    const t = clean(iEdit.text);
    if (!t) return setIErr('Type the name.');
    if (taken(t, iEdit.at)) return setIErr(`${t} is already on the list.`);
    const was = inter[iEdit.at];
    setInter((l) => l.map((x, i) => (i === iEdit.at ? t : x)));
    setHideD((h) => h.map((x) => (x === was ? t : x)));
    setIEdit(null);
    setIErr('');
  };
  // ehr#682 (V141 V126): one setting, Generate notes with AI On or Off
  const [d, setD] = useState<NoteSettings>(() => ({ mode: { ...V141_NOTE_MODE }, ai: current.ai }));
  const [p, setP] = useState<PracticeNoteSettings | null>(() => practice?.value ?? null);
  const pPatch = practice && p ? practiceNotesPatch(p, practice.value) : null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // ehr#437 (Chase 10-17): leaving with changes not saved (✕, Escape, outside, Cancel, or the
  // Show the list link) asks Save, Don't save or Go back. `ask` holds where it was going.
  const [ask, setAsk] = useState<{ go: () => void } | null>(null);
  const goBack = useCallback(() => setAsk(null), []);
  const dirty =
    (!aiBySupervisor && settingsChanged(d, current)) ||
    !!pPatch ||
    (admin && JSON.stringify(inter) !== JSON.stringify(interventions)) ||
    hideChanged ||
    codeChanged;
  const leave = (go: () => void) => {
    if (busy) return;
    if (dirty) setAsk({ go });
    else go();
  };
  const requestClose = () => leave(onClose);
  // ehr#682 (V141 V127): Save for this client keeps the Show / hide choice as this client's own;
  // Save for all clients makes it the all-clients list. Plain Save (Note tab) is for this client.
  const save = (then: () => void = onClose, scope: 'client' | 'all' = 'client') => {
    setAsk(null);
    setBusy(true);
    setErr('');
    const interChanged = admin && JSON.stringify(inter) !== JSON.stringify(interventions);
    const applyHide =
      hideOn && (hideChanged || (scope === 'all' && hidden!.clients[contactId!] !== undefined));
    const nextHide = applyHide ? saveHidden(hideBase, contactId!, hideD, scope) : hideBase;
    // ehr#725: the practice's codes with this kind's Default billing code changed
    const nextCodes = codeChanged ? { ...defaultCode!.codes, [defaultCode!.kind]: code } : null;
    const body = {
      ...(interChanged ? { interventions: inter } : {}),
      ...(hideOn && (interChanged || applyHide) ? { interventionsHidden: nextHide } : {}),
      ...(nextCodes ? { serviceCodes: nextCodes } : {}),
    };
    (Object.keys(body).length
      ? api.put('/practice/clinical', body).then(() => {
          if (interChanged) onInterventions!(inter);
          if (body.interventionsHidden) onHidden!(nextHide);
          if (nextCodes) defaultCode!.onSaved(nextCodes);
        })
      : Promise.resolve()
    )
      .then(() =>
        pPatch
          ? api
              .put<PracticeNoteSettings>('/practice/note-settings', pPatch)
              .then((v) => practice!.onSaved(v))
          : undefined,
      )
      // an associate's AI is their supervisor's to set, so it isn't saved as their own
      .then(() =>
        aiBySupervisor ? view : api.put<NotePhrasesView>('/note-phrases/note-settings', d),
      )
      .then((v) => {
        onSaved(v);
        then();
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save your note settings.'),
      )
      .finally(() => setBusy(false));
  };
  // ehr#682 (V141 drawNoteSettingsBody): Note and Quick add for everyone; Interventions for admins
  // ehr#729: one tab (a Skills / rehab note's Note tab) has no strip
  const tabStrip = tabs.length > 1 && (
    <div className="pn-seg pn-ns-tabs" role="tablist" aria-label="Note settings">
      {tabs.map(([k, t]) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={tab === k}
          aria-pressed={tab === k}
          onClick={() => pickTab(k)}
        >
          {t}
        </button>
      ))}
    </div>
  );
  const confirm = ask && (
    <ConfirmDialog
      message={
        <>
          <b>Save your changes?</b>
          <p className="pn-modal-p">You changed note settings and haven&rsquo;t saved them.</p>
        </>
      }
      cancelLabel="Go back"
      altLabel="Don't save"
      onAlt={() => {
        const { go } = ask;
        setAsk(null);
        go();
      }}
      confirmLabel="Save"
      danger={false}
      focusConfirm
      busy={busy}
      onConfirm={() => save(ask.go)}
      onClose={goBack}
    />
  );
  // ehr#732: on a note with no Quick add card the tab is Helpers (its side cards' Show switches)
  const helpers = kindSettings?.side;
  if (tab === 'qa' && helpers)
    return (
      <>
        <HelpersWindow
          view={view}
          contactId={quick.contactId}
          offered={helpers.cards}
          title={helpers.title}
          onClose={requestClose}
          onSaved={(v) => {
            quick.onApplied(v, []);
            onClose();
          }}
          tabs={tabStrip}
          leaveRef={qaLeave}
        />
        {confirm}
      </>
    );
  // ehr#682 (V141 V104): the Quick add tab is the Quick add settings, with its own footer
  // (Save for this client / Save for all clients saves and closes, as in V141)
  if (tab === 'qa')
    return (
      <>
        <QuickSettingsWindow
          view={view}
          contactId={quick.contactId}
          contactName={quick.contactName}
          couples={quick.couples}
          ownRatingsOnly={quick.ownRatingsOnly}
          noMeasures={quick.noMeasures}
          onClose={requestClose}
          onApplied={quick.onApplied}
          onSaved={(v, added) => {
            quick.onApplied(v, added);
            onClose();
          }}
          tabs={tabStrip}
          offCards={quick.offCards}
          leaveRef={qaLeave}
          railOff={quick.railOff}
          dotOff={quick.dotOff}
          {...(quick.sideCards ? { sideCards: quick.sideCards } : {})}
        />
        {confirm}
      </>
    );
  return (
    <>
      <Modal
        // V141 V112: the title follows the tab
        title={tab === 'inter' ? 'Interventions' : 'Note settings'}
        onClose={requestClose}
        className="pn-ns-modal"
        actions={
          <>
            {/* ehr#682 (V141 V123 / V133): the reports open from the footer (this window closes) */}
            {onReport && (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => leave(() => onReport('timeliness'))}
                  disabled={busy}
                >
                  Sign timeliness
                </button>
                <button
                  type="button"
                  className="btn pn-ns-foot-split"
                  onClick={() => leave(() => onReport('supervision'))}
                  disabled={busy}
                >
                  Supervision
                </button>
              </>
            )}
            <button type="button" className="btn" onClick={requestClose} disabled={busy}>
              Cancel
            </button>
            {hideOn && tab === 'inter' ? (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => save(onClose, 'client')}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : 'Save for this client'}
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => save(onClose, 'all')}
                  disabled={busy}
                >
                  Save for all clients
                </button>
              </>
            ) : (
              <button type="button" className="btn primary" onClick={() => save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </>
        }
      >
        {tabStrip}
        {tab === 'inter' && (
          <div className="pn-ns" data-test="inter-list">
            <div className="pn-ns-h">
              <b>Interventions</b>
              <span className="pn-ns-muted">{inter.length} on the practice list</span>
            </div>
            <p className="pn-ns-muted pn-ns-note">
              The &ldquo;Interventions used&rdquo; checklist on every note. Notes already written
              keep what they recorded.
            </p>
            {hideOn && (
              <p className="pn-ns-muted pn-ns-note" data-test="inter-hide-help">
                Untick Show to keep an intervention off the checklist. A ticked one still shows, and
                &ldquo;Search or add an intervention&rdquo; still finds it.{' '}
                <b>Save for this client</b> keeps the choice for {clientFirst};{' '}
                <b>Save for all clients</b> uses it for every client without a choice of their own.
              </p>
            )}
            <ul className="pn-inter-list">
              {inter.map((t, i) => (
                <li key={t} className="pn-inter-row">
                  {iEdit?.at === i ? (
                    <>
                      <input
                        className="input"
                        value={iEdit.text}
                        maxLength={60}
                        aria-label="Name"
                        autoFocus
                        onChange={(e) => setIEdit({ at: i, text: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEdit();
                          }
                        }}
                      />
                      <button type="button" className="btn small" onClick={() => setIEdit(null)}>
                        Cancel
                      </button>
                      <button type="button" className="btn small primary" onClick={saveEdit}>
                        Save
                      </button>
                    </>
                  ) : iDel === i ? (
                    <>
                      <span className="pn-inter-name">
                        <b>Delete &ldquo;{t}&rdquo;?</b>{' '}
                        <span className="pn-ns-muted">Signed notes keep it.</span>
                      </span>
                      <button type="button" className="btn small" onClick={() => setIDel(null)}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn small danger"
                        onClick={() => {
                          setInter((l) => l.filter((_, j) => j !== i));
                          setOrigin((l) => l.filter((_, j) => j !== i));
                          setHideD((h) => h.filter((x) => x !== t));
                          setIDel(null);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="pn-inter-name">{t}</span>
                      {hideOn && (
                        <label className="pn-inter-show">
                          <input
                            type="checkbox"
                            checked={!hideD.includes(t)}
                            aria-label={`Show ${t}`}
                            onChange={(e) =>
                              setHideD((h) =>
                                e.target.checked
                                  ? h.filter((x) => x !== t)
                                  : h.includes(t)
                                    ? h
                                    : [...h, t],
                              )
                            }
                          />
                          Show
                        </label>
                      )}
                      <button
                        type="button"
                        className="pn-link"
                        onClick={() => {
                          setIDel(null);
                          setIErr('');
                          setIEdit({ at: i, text: t });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="pn-link pn-link-bad"
                        onClick={() => {
                          setIEdit(null);
                          setIDel(i);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <div className="pn-inter-add">
              <input
                className="input"
                placeholder="New intervention"
                maxLength={60}
                value={iNew}
                aria-label="New intervention"
                onChange={(e) => setINew(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addInter();
                  }
                }}
              />
              <button type="button" className="btn small primary" onClick={addInter}>
                Add
              </button>
            </div>
            {iErr && (
              <p className="pn-inline-err" role="alert">
                {iErr}
              </p>
            )}
            {err && (
              <p className="pn-inline-err" role="alert">
                {err}
              </p>
            )}
          </div>
        )}
        <div className="pn-ns" hidden={tab !== 'note'}>
          {review && (
            <div className="pn-ns-sec" data-test="ns-review">
              <div className="pn-ns-h">
                <b>Supervisor review</b>
              </div>
              <div className="pn-ns-review">
                <div className="pn-seg pn-ns-seg" role="group" aria-label="Show changes">
                  {(
                    [
                      ['changes', 'Show changes'],
                      ['final', 'Final text'],
                    ] as const
                  ).map(([k, t]) => (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={review.view === k}
                      onClick={() => review.onView(k)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <button type="button" className="pn-link" onClick={() => leave(review.onHistory)}>
                  Comment history
                </button>
              </div>
            </div>
          )}
          {/* ehr#682 (V141 V120, V126): the one AI setting; On generates the note when it opens */}
          <div className="pn-ns-sec" data-test="ns-ai">
            <div className="pn-ns-h">
              <b>Generate notes with AI</b>
              {aiBySupervisor && <span className="pn-ns-muted">Set by your supervisor</span>}
              <span className="pn-sp" />
              <div className="pn-seg pn-ns-seg" role="group" aria-label="Generate notes with AI">
                {(
                  [
                    ['open', 'On'],
                    ['off', 'Off'],
                  ] as const
                ).map(([k, t]) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={(k === 'off') === (d.ai === 'off')}
                    disabled={aiBySupervisor}
                    onClick={() => setD((x) => ({ ...x, ai: k }))}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="pn-ns-sec">
            <div className="pn-ns-h">
              <b>Keyboard shortcuts</b>
              <span className="pn-sp" />
              <button type="button" className="pn-link" onClick={() => leave(onShortcuts)}>
                Show the list
              </button>
            </div>
          </div>
          {p && (
            <PracticeNoteSettingsSection
              value={p}
              onChange={setP}
              goalRules={goalRules}
              caseGoals={caseGoals}
            >
              {practice?.discharge && <DischargePracticeSettings value={p} onChange={setP} />}
              {/* ehr#725: a case management note's Default billing code; ehr#729 (V7 V3): other
                  kinds' own default code row */}
              {defaultCode &&
                (defaultCode.kind === 'Case management' ? (
                  <DefaultCodeRow value={code} onChange={setCode} />
                ) : (
                  <KindCodeRow kind={defaultCode.kind} value={code} onChange={setCode} />
                ))}
            </PracticeNoteSettingsSection>
          )}
          {err && (
            <p className="pn-inline-err" role="alert">
              {err}
            </p>
          )}
        </div>
      </Modal>
      {confirm}
    </>
  );
}

export type NoteSettingsTab = 'note' | 'qa' | 'inter';
/**
 * ehr#682 (V141 tabDefs): Note, Quick add, then Interventions for the practice's admins.
 * ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5): on a note with no Quick add the tab is Side cards.
 * ehr#729: a kind with tabs of its own (kindSettingsOf) renames or drops Quick add and may drop
 * Interventions (a Skills / rehab note has the Note tab only).
 */
export function noteSettingsTabs(
  admin: boolean,
  sideCards = false,
  kind?: KindSettings,
): [NoteSettingsTab, string][] {
  const side = kind ? kind.side : { title: sideCards ? 'Side cards' : 'Quick add' };
  const inter = admin && (kind ? kind.interventions : true);
  return [
    ['note', 'Note'],
    ...(side ? ([['qa', side.title]] as [NoteSettingsTab, string][]) : []),
    ...(inter ? ([['inter', 'Interventions']] as [NoteSettingsTab, string][]) : []),
  ];
}
/** What the Quick add tab needs from the note. */
export interface QuickTabProps {
  contactId: string;
  contactName: string;
  couples: boolean;
  /** ehr#708: the side cards this note doesn't have (a discharge summary's), so no Show switch. */
  offCards?: readonly string[];
  /** ehr#724 (ADMIN_PHONE_NOTE_10-30-V7): Ratings lists only your own; no Show Measures switch. */
  ownRatingsOnly?: boolean;
  noMeasures?: boolean;
  /** Quick add settings saved (Show Quick add, a dot phrase, Save): items newly on every note for this client go on this note. */
  onApplied: (v: NotePhrasesView, added: QuickItem[]) => void;
  /** ehr#725 (CASE_MANAGEMENT_NOTE V9 16 A): the Show switches this kind of note doesn't offer. */
  railOff?: readonly RailSwitch[];
  /** ehr#725 (V9 18 A): no Dot phrases tab on this kind of note. */
  dotOff?: boolean;
  /**
   * ehr#728: the rail cards a note with no Quick add has (a Collateral contact note: Last session
   * and Generate Notes). Given, the tab is Side cards: only their Show switches, saved for that
   * note kind only.
   */
  sideCards?: SideCardsTab;
}

/** ehr#682: whether the window's setting differs from what the note uses now (AI On or Off). */
export function settingsChanged(a: NoteSettings, b: NoteSettings): boolean {
  return (a.ai === 'off') !== (b.ai === 'off');
}

/** V81 SHORTCUTS (Cmd on a Mac, Ctrl elsewhere). */
export const MOD_KEY =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? 'Cmd' : 'Ctrl';
export const SHORTCUTS: [string, string][] = [
  [`${MOD_KEY} + S`, 'Save Draft'],
  [`${MOD_KEY} + Enter`, 'Sign Off Note (Submit to Supervisor when your notes are co-signed)'],
  [`${MOD_KEY} + .`, 'Jump to the next item left to sign'],
  ['Alt + ↓', 'Next section'],
  ['Alt + ↑', 'Previous section'],
  ['. then a name', 'Put in a dot phrase (like .mse), then Enter'],
  ['Tab', 'Next __ blank in a phrase'],
  // ehr#523
  ['Hold Right Alt', 'Dictate into the box you are in (when dictation is on); let go to stop'],
  ['?', 'This list (outside a box)'],
];

export function ShortcutsWindow({ onClose }: { onClose: () => void }) {
  return (
    <Modal
      title="Keyboard shortcuts"
      onClose={onClose}
      className="pn-keys-modal"
      actions={
        <button type="button" className="btn primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <table className="pn-keys">
        <tbody>
          {SHORTCUTS.map(([k, t]) => (
            <tr key={k}>
              <td>
                <kbd>{k}</kbd>
              </td>
              <td>{t}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}
